import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { useApiMutation } from '../hooks/useApiMutation';
import { toRecurring, toTicket } from '../utils/maintenance';

const KEY = queryKeys.maintenance.lists();

const adapt = (data) => ({
  tickets: (data?.tickets || []).map(toTicket),
  recurring: (data?.recurring || []).map(toRecurring)
});

const patchList = (list, id, patch) => list.map((x) => (x._id === id ? { ...x, ...patch } : x));

const useMaintenanceMutation = (opts) => useApiMutation({ invalidate: [queryKeys.maintenance.all], ...opts });

export function useMaintenance(params = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.maintenance.list(params),
    queryFn: () => portalApi.maintenance(params),
    select: adapt,
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000
  });
}

export function useCreateTicket() {
  return useMaintenanceMutation({
    mutationFn: portalApi.createMaintenanceTicket,
    success: (data) => (data.ticket?.ticketNumber ? `Ticket ${data.ticket.ticketNumber} created` : 'Ticket created'),
    error: 'Could not create this ticket'
  });
}

export function useUpdateTicket() {
  return useMaintenanceMutation({
    mutationFn: ({ id, ...patch }) => portalApi.updateMaintenanceTicket(id, patch),
    optimistic: ({ id, ...patch }) => [
      { key: KEY, update: (data) => ({ ...data, tickets: patchList(data.tickets, id, patch) }) }
    ],
    error: 'Could not update this ticket'
  });
}

export function useDeleteTicket() {
  return useMaintenanceMutation({
    mutationFn: (id) => portalApi.removeMaintenanceTicket(id),
    optimistic: (id) => [{ key: KEY, update: (data) => ({ ...data, tickets: data.tickets.filter((t) => t._id !== id) }) }],
    success: 'Ticket deleted',
    error: 'Could not delete this ticket'
  });
}

export function useAddTicketNote() {
  return useMaintenanceMutation({
    mutationFn: ({ id, text }) => portalApi.addMaintenanceNote(id, text),
    error: 'Could not add this note'
  });
}

export function useSaveRecurring() {
  return useMaintenanceMutation({
    mutationFn: ({ id, ...values }) => (id ? portalApi.updateRecurring(id, values) : portalApi.createRecurring(values)),
    success: (_data, { id }) => (id ? 'Service updated' : 'Service scheduled'),
    error: 'Could not save this service'
  });
}

export function useDeleteRecurring() {
  return useMaintenanceMutation({
    mutationFn: portalApi.removeRecurring,
    optimistic: (id) => [
      { key: KEY, update: (data) => ({ ...data, recurring: data.recurring.filter((r) => r._id !== id) }) }
    ],
    success: 'Service removed',
    error: 'Could not remove this service'
  });
}
